import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const SubscriptionSection = () => {
  const [currentPlan] = useState('premium');

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      features: [
        '5 practice interviews per month',
        'Basic feedback and scoring',
        'Limited question bank access',
        'Community support'
      ],
      icon: 'Zap',
      color: 'var(--color-muted-foreground)'
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$29',
      period: 'per month',
      features: [
        'Unlimited practice interviews',
        'Advanced AI feedback & analysis',
        'Full question bank access',
        'Priority mentor consultations',
        'Performance analytics dashboard',
        'Custom interview scenarios'
      ],
      icon: 'Crown',
      color: 'var(--color-primary)',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$99',
      period: 'per month',
      features: [
        'Everything in Premium',
        'Dedicated account manager',
        'Custom integration support',
        'Team collaboration features',
        'Advanced reporting & insights',
        'White-label options'
      ],
      icon: 'Building',
      color: 'var(--color-secondary)'
    }
  ];

  const billingHistory = [
    {
      id: 1,
      date: '11/01/2025',
      description: 'Premium Plan - Monthly',
      amount: '$29.00',
      status: 'paid',
      invoice: 'INV-2025-001'
    },
    {
      id: 2,
      date: '10/01/2025',
      description: 'Premium Plan - Monthly',
      amount: '$29.00',
      status: 'paid',
      invoice: 'INV-2025-002'
    },
    {
      id: 3,
      date: '09/01/2025',
      description: 'Premium Plan - Monthly',
      amount: '$29.00',
      status: 'paid',
      invoice: 'INV-2025-003'
    }
  ];

  const handleDownloadInvoice = (invoice) => {
    console.log('Downloading invoice:', invoice);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="CreditCard" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground">
              Subscription & Billing
            </h2>
            <p className="text-sm text-muted-foreground">
              Manage your plan and payment history
            </p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans?.map((plan) => (
            <div
              key={plan?.id}
              className={`
                relative bg-muted/30 border rounded-lg p-6 transition-all duration-150 ease-out
                ${currentPlan === plan?.id 
                  ? 'border-primary shadow-elevation-2' 
                  : 'border-border hover:border-muted hover:shadow-elevation-1'
                }
              `}
            >
              {plan?.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                    Current Plan
                  </span>
                </div>
              )}

              <div className="flex items-center justify-center mb-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${plan?.color}15` }}
                >
                  <Icon name={plan?.icon} size={24} color={plan?.color} />
                </div>
              </div>

              <h3 className="text-xl font-heading font-bold text-foreground text-center mb-2">
                {plan?.name}
              </h3>

              <div className="text-center mb-6">
                <span className="text-3xl font-heading font-bold text-foreground">
                  {plan?.price}
                </span>
                <span className="text-sm text-muted-foreground ml-1">
                  {plan?.period}
                </span>
              </div>

              <ul className="space-y-3 mb-6">
                {plan?.features?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon 
                      name="Check" 
                      size={16} 
                      color="var(--color-success)" 
                      className="flex-shrink-0 mt-0.5"
                    />
                    <span className="text-sm text-muted-foreground">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                variant={currentPlan === plan?.id ? "outline" : "default"}
                fullWidth
                disabled={currentPlan === plan?.id}
              >
                {currentPlan === plan?.id ? 'Current Plan' : 'Upgrade'}
              </Button>
            </div>
          ))}
        </div>

        <div className="bg-muted/30 border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Icon name="Receipt" size={20} color="var(--color-foreground)" />
              <h3 className="text-sm font-medium text-foreground">
                Billing History
              </h3>
            </div>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
              Export All
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground pb-3">
                    Date
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground pb-3">
                    Description
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground pb-3">
                    Amount
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground pb-3">
                    Status
                  </th>
                  <th className="text-right text-xs font-medium text-muted-foreground pb-3">
                    Invoice
                  </th>
                </tr>
              </thead>
              <tbody>
                {billingHistory?.map((item) => (
                  <tr key={item?.id} className="border-b border-border last:border-b-0">
                    <td className="py-4 text-sm text-foreground">
                      {item?.date}
                    </td>
                    <td className="py-4 text-sm text-foreground">
                      {item?.description}
                    </td>
                    <td className="py-4 text-sm font-medium text-foreground">
                      {item?.amount}
                    </td>
                    <td className="py-4">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-success bg-success/10 px-2 py-1 rounded">
                        <Icon name="CheckCircle" size={12} color="var(--color-success)" />
                        Paid
                      </span>
                    </td>
                    <td className="py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Download"
                        onClick={() => handleDownloadInvoice(item?.invoice)}
                      >
                        {item?.invoice}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-foreground mb-1">
                Payment Method
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Your card ending in ****4242 will be charged on 12/01/2025
              </p>
              <Button variant="outline" size="sm">
                Update Payment Method
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSection;